import { DEFAULT_AVATAR_RULES } from './defaults';
import { AVATAR_RULES_REGISTRY } from './registry';

/**
 * @typedef {Object} AvatarRules
 * @property {{ spellbook: number, atlas: number }} openingHand
 *   Cards drawn from each deck at game start, before any mulligan.
 * @property {{ maxCards: number, allowedCount: number }} mulligan
 *   Limits on the single mulligan procedure per match.
 * @property {number} startingLife   Avatar HP at match start.
 * @property {number} startingMana   Player mana pool at match start.
 * @property {object} [hooks]        Future extension. Imperative lifecycle callbacks.
 */

/**
 * Resolve the full rule set for a given avatar card instance. Returns
 * the defaults merged with any avatar-specific overrides from the
 * registry. Safe to call with a null/undefined avatar (e.g. during the
 * loading screen before a deck is spawned). Returns the pure defaults.
 *
 * The resolved object is a fresh plain object, not a frozen one, so
 * callers may mutate it safely if they need to further override for a
 * one-off situation (e.g. format-specific rules layered on top). The
 * defaults themselves remain immutable. StructuredClone ensures no
 * shared reference back to the frozen source.
 *
 * @param {object|null|undefined} avatarInstance
 *   A card instance of type Avatar, matching the shape produced by
 *   `createCardInstance` in gameState.js. Must at minimum carry
 *   `cardId` (e.g. "sorcery-spellslinger").
 * @returns {AvatarRules}
 */
export function resolveAvatarRules(avatarInstance) {
  // In-game card instances (from `createCardInstance` in gameState.js)
  // carry `cardId`; raw catalog cards (from `normalizeCards.js`, what the
  // deck editor passes around) only carry `unique_id`. Both are the same
  // string (e.g. `sorcery-magician`). Accept either so callers don't
  // have to repackage catalog cards into a fake `{ cardId }` wrapper.
  const cardId = avatarInstance?.cardId || avatarInstance?.unique_id;
  const overrides = (cardId && AVATAR_RULES_REGISTRY[cardId]) || {};
  // Two-faced avatars (Druid) declare a `back: { ... }` override that
  // applies only when the in-play instance has been flipped to its
  // back face. Catalog cards (no `faceDown`) always resolve to the
  // front-face rules. Keeps deckbuilder behaviour unchanged.
  const faceOverride = (avatarInstance?.faceDown && overrides.back) ? overrides.back : null;
  const baseMerged = deepMergeRules(DEFAULT_AVATAR_RULES, overrides);
  return faceOverride ? deepMergeRules(baseMerged, faceOverride) : baseMerged;
}

/**
 * Shallow sugar: pull the opening-hand counts for an avatar.
 * Exists because this is the most common caller pattern and makes the
 * call site read clearly at the draw loop.
 */
export function getOpeningHand(avatarInstance) {
  return resolveAvatarRules(avatarInstance).openingHand;
}

/**
 * Shallow sugar: pull the mulligan config for an avatar.
 */
export function getMulliganRules(avatarInstance) {
  return resolveAvatarRules(avatarInstance).mulligan;
}

// ── Internal ──────────────────────────────────────────────────────────

// Intentionally minimal: two levels deep is all any avatar rule ever
// needs (e.g. openingHand.spellbook, mulligan.maxCards). A generic
// recursive merge would be over-engineered for a fixed, documented
// rule shape. Adding a new nested category is rare and requires
// updating this function anyway.
function deepMergeRules(base, override) {
  const result = { ...base };
  for (const key of Object.keys(override)) {
    const baseVal = base[key];
    const overrideVal = override[key];
    if (
      baseVal && typeof baseVal === 'object' && !Array.isArray(baseVal)
      && overrideVal && typeof overrideVal === 'object' && !Array.isArray(overrideVal)
    ) {
      result[key] = { ...baseVal, ...overrideVal };
    } else {
      result[key] = overrideVal;
    }
  }
  return result;
}
